<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-[60] overflow-y-auto" @keydown="handleDialogKeydown">
        <div class="fixed inset-0 bg-black/60"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <section
            ref="dialogRef"
            role="dialog"
            aria-modal="true"
            aria-labelledby="official-publisher-manager-title"
            tabindex="-1"
            class="relative flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl"
          >
            <!-- 固定头部 -->
            <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4 shrink-0">
              <h3 id="official-publisher-manager-title" class="text-lg font-semibold text-slate-900">
                {{ dialogTitle }}
              </h3>
              <button
                type="button"
                title="关闭"
                aria-label="关闭官方发布者管理"
                :disabled="submitting"
                class="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
                @click="handleClose"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 可滚动正文 -->
            <div class="flex-1 overflow-y-auto px-6 py-4">
              <!-- 列表视图 -->
              <template v-if="viewMode === 'list'">
                <div v-if="loading" class="space-y-3">
                  <div v-for="index in 3" :key="index" class="h-14 animate-pulse rounded-lg bg-slate-50"></div>
                </div>
                <div v-else-if="loadFailed" class="py-10 text-center">
                  <p class="text-sm text-slate-500">官方发布者列表加载失败</p>
                  <button type="button" class="btn-secondary mt-4" @click="loadList">重试</button>
                </div>
                <div v-else-if="list.length === 0" class="py-10 text-center">
                  <p class="text-sm text-slate-500">暂无官方发布者，点击下方按钮新增</p>
                </div>
                <ul v-else class="divide-y divide-slate-100">
                  <li v-for="item in list" :key="item.id" class="flex items-center gap-3 py-3">
                    <img
                      :src="item.avatarUrl"
                      :alt="item.nickname"
                      class="h-10 w-10 shrink-0 rounded-full object-cover"
                    />
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-sm font-medium text-slate-800" :title="item.nickname">
                        {{ item.nickname }}
                      </p>
                      <p class="text-xs text-slate-400">排序 {{ item.sort }}</p>
                    </div>
                    <label class="form-switch" :title="item.status === 1 ? '点击停用' : '点击启用'">
                      <input
                        type="checkbox"
                        class="form-switch-input"
                        :checked="item.status === 1"
                        :disabled="rowLoadingId === item.id"
                        @change="handleStatus(item)"
                      >
                      <span class="form-switch-track"></span>
                    </label>
                    <button
                      type="button"
                      class="table-action-button is-primary"
                      title="编辑"
                      :disabled="rowLoadingId === item.id"
                      @click="handleEdit(item)"
                    >
                      <el-icon :size="16"><Edit /></el-icon>
                    </button>
                    <button
                      type="button"
                      class="table-action-button is-danger"
                      title="删除"
                      :disabled="rowLoadingId === item.id"
                      @click="handleDelete(item)"
                    >
                      <el-icon :size="16"><Delete /></el-icon>
                    </button>
                  </li>
                </ul>
              </template>

              <!-- 表单视图（新增/编辑复用，不打开第三层弹窗） -->
              <div v-else class="space-y-4">
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700">
                    昵称 <span class="text-red-500">*</span>
                  </label>
                  <input
                    ref="nicknameInput"
                    v-model="form.nickname"
                    type="text"
                    maxlength="50"
                    placeholder="如：品牌官方"
                    class="form-input w-full"
                  />
                </div>
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700">
                    头像 <span class="text-red-500">*</span>
                  </label>
                  <div class="flex items-center gap-3">
                    <button
                      type="button"
                      title="上传头像"
                      class="flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-slate-300 transition-colors hover:border-primary-400"
                      @click="avatarInput?.click()"
                    >
                      <img v-if="avatarPreview" :src="avatarPreview" class="h-full w-full object-cover" />
                      <svg v-else class="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                      </svg>
                    </button>
                    <span class="text-xs text-slate-400">点击上传头像，保存时才会提交</span>
                  </div>
                  <input ref="avatarInput" type="file" accept="image/*" class="hidden" @change="handleAvatarChange" />
                </div>
                <div>
                  <label class="mb-1 block text-sm font-medium text-slate-700">排序值（越小越靠前）</label>
                  <input
                    v-model="form.sort"
                    type="number"
                    min="0"
                    step="1"
                    class="form-input w-full"
                  />
                </div>
              </div>
            </div>

            <!-- 固定底部 -->
            <div class="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4 shrink-0">
              <template v-if="viewMode === 'list'">
                <button type="button" class="btn-secondary" @click="handleClose">关闭</button>
                <button type="button" class="btn-primary" @click="handleCreate">新增发布者</button>
              </template>
              <template v-else>
                <button type="button" class="btn-secondary" :disabled="submitting" @click="handleCancelForm">取消</button>
                <button type="button" class="btn-primary" :disabled="submitting" @click="handleSave">
                  {{ submitting ? '保存中...' : '保存' }}
                </button>
              </template>
            </div>
          </section>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Delete, Edit } from '@element-plus/icons-vue'
import {
  createOfficialPublisher,
  deleteOfficialPublisher,
  getOfficialPublishers,
  updateOfficialPublisher,
  updateOfficialPublisherStatus
} from '@/api/circle'
import { uploadFile } from '@/api/file'
import { compressImage } from '@/utils/imageCompress'

const props = defineProps({ show: Boolean })
const emit = defineEmits(['update:show', 'changed'])

const dialogRef = ref(null)
const avatarInput = ref(null)
const nicknameInput = ref(null)
const viewMode = ref('list')
const list = ref([])
const loading = ref(false)
const loadFailed = ref(false)
const submitting = ref(false)
const rowLoadingId = ref(null)
const editingId = ref(null)
const pendingAvatarFile = ref(null)
const avatarPreview = ref('')
const form = reactive({ nickname: '', avatarUrl: '', sort: 0 })
let previousFocusedElement = null
let avatarPreviewIsObjectUrl = false

const dialogTitle = computed(() => {
  if (viewMode.value === 'create') return '新增官方发布者'
  if (viewMode.value === 'edit') return '编辑官方发布者'
  return '管理官方发布者'
})

watch(() => props.show, async (val) => {
  if (val) {
    previousFocusedElement = document.activeElement
    viewMode.value = 'list'
    resetFormState()
    await loadList()
    await nextTick()
    dialogRef.value?.focus()
  } else {
    releaseAvatarPreview()
  }
})

const loadList = async () => {
  loading.value = true
  loadFailed.value = false
  try {
    const data = await getOfficialPublishers()
    list.value = Array.isArray(data) ? data : []
  } catch {
    list.value = []
    loadFailed.value = true
  } finally {
    loading.value = false
  }
}

const releaseAvatarPreview = () => {
  if (avatarPreviewIsObjectUrl && avatarPreview.value) {
    URL.revokeObjectURL(avatarPreview.value)
  }
  avatarPreviewIsObjectUrl = false
  avatarPreview.value = ''
}

const resetFormState = () => {
  form.nickname = ''
  form.avatarUrl = ''
  form.sort = 0
  editingId.value = null
  pendingAvatarFile.value = null
  releaseAvatarPreview()
}

const handleCreate = () => {
  resetFormState()
  viewMode.value = 'create'
}

const handleEdit = (item) => {
  resetFormState()
  form.nickname = item.nickname
  form.avatarUrl = item.avatarUrl
  form.sort = item.sort ?? 0
  avatarPreview.value = item.avatarUrl
  editingId.value = item.id
  viewMode.value = 'edit'
}

const handleCancelForm = () => {
  if (submitting.value) return
  resetFormState()
  viewMode.value = 'list'
}

const handleAvatarChange = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  if (!file.type.startsWith('image/')) {
    ElMessage.warning('请选择图片文件')
    return
  }
  const compressed = await compressImage(file)
  pendingAvatarFile.value = compressed
  releaseAvatarPreview()
  avatarPreview.value = URL.createObjectURL(compressed)
  avatarPreviewIsObjectUrl = true
  event.target.value = ''
}

const handleSave = async () => {
  if (submitting.value) return
  const nickname = form.nickname.trim()
  if (!nickname) {
    ElMessage.warning('发布者昵称不能为空')
    nicknameInput.value?.focus()
    return
  }
  if (nickname.length > 50) {
    ElMessage.warning('发布者昵称长度不能超过50个字符')
    nicknameInput.value?.focus()
    return
  }
  const sort = form.sort === '' || form.sort == null ? 0 : Number(form.sort)
  if (!Number.isInteger(sort) || sort < 0) {
    ElMessage.warning('排序值不能小于0')
    return
  }

  submitting.value = true
  try {
    // 先上传头像，成功后才调用新增或更新接口；上传失败保留表单和本地预览
    let avatarUrl = form.avatarUrl.trim()
    if (pendingAvatarFile.value) {
      const result = await uploadFile(pendingAvatarFile.value, 'circle', 'public')
      avatarUrl = result.url || result
    }
    if (!avatarUrl) {
      ElMessage.warning('请上传发布者头像')
      return
    }
    if (avatarUrl.length > 500) {
      ElMessage.warning('发布者头像长度不能超过500个字符')
      return
    }
    const payload = { nickname, avatarUrl, sort }
    if (viewMode.value === 'edit') {
      await updateOfficialPublisher(editingId.value, payload)
    } else {
      await createOfficialPublisher(payload)
    }
    ElMessage.success('保存成功')
    emit('changed')
    resetFormState()
    viewMode.value = 'list'
    await loadList()
  } catch (error) {
    ElMessage.error(error.message || '保存失败')
    if (error.message && error.message.includes('昵称')) {
      nicknameInput.value?.focus()
    }
  } finally {
    submitting.value = false
  }
}

const handleStatus = async (item) => {
  const targetStatus = item.status === 1 ? 0 : 1
  rowLoadingId.value = item.id
  try {
    await updateOfficialPublisherStatus(item.id, { status: targetStatus })
    ElMessage.success(targetStatus === 1 ? '已启用' : '已停用')
    emit('changed')
    await loadList()
  } catch (error) {
    ElMessage.error(error.message || '状态更新失败')
  } finally {
    rowLoadingId.value = null
  }
}

const handleDelete = async (item) => {
  try {
    await ElMessageBox.confirm(
      `删除后历史动态仍展示原有昵称和头像，确认删除发布者“${item.nickname}”吗？`,
      '删除官方发布者',
      { confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning' }
    )
  } catch {
    return
  }
  rowLoadingId.value = item.id
  try {
    await deleteOfficialPublisher(item.id)
    ElMessage.success('删除成功')
    emit('changed')
    await loadList()
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  } finally {
    rowLoadingId.value = null
  }
}

const handleClose = () => {
  if (submitting.value) return
  emit('update:show', false)
  if (previousFocusedElement && typeof previousFocusedElement.focus === 'function') {
    previousFocusedElement.focus()
  }
  previousFocusedElement = null
}

// 键盘焦点约束：Escape 返回/关闭，Tab 在弹窗内循环
const handleDialogKeydown = (event) => {
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    if (viewMode.value === 'list') {
      handleClose()
    } else {
      handleCancelForm()
    }
    return
  }
  if (event.key !== 'Tab') return
  const root = dialogRef.value
  if (!root) return
  const selectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',')
  const focusable = Array.from(root.querySelectorAll(selectors))
    .filter((el) => el.offsetParent !== null)
  if (focusable.length === 0) {
    event.preventDefault()
    root.focus()
    return
  }
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement
  if (event.shiftKey && (active === first || !root.contains(active))) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && (active === last || !root.contains(active))) {
    event.preventDefault()
    first.focus()
  }
}

onBeforeUnmount(() => {
  releaseAvatarPreview()
})
</script>
